'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Bot, LayoutDashboard } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTeams, useProjects } from '../../hooks/queries/useTeams';
import { useMeProjects } from '../../hooks/queries/useMeProjects';
import { useAgentConfigs } from '../../hooks/queries/useAgentConfigs';
import { useActiveDaemonTriggers } from '../../hooks/queries/useDaemonTriggers';
import { useHeartbeat } from '../../hooks/queries/useHeartbeat';
import { useProjectPermissions } from '../../hooks/useProjectPermissions';
import { useNavigationBadges } from '../../hooks/queries/useNavigationBadges';
import { getProjectNavPath, type ProjectNavPermission } from '../../lib/projectNavRoutes';
import {
  useMarkUnifiedNotificationsForTargetAsReadMutation,
  useUnifiedUnreadCount,
} from '../../hooks/queries/useNotifications';
import { useDaemons, useSharedDaemons } from '../../hooks/queries/useDaemons';
import {
  useMergedRunnerModels,
  useRunnerModelCatalogs,
  useRunnerModels,
  useRunnerModelsForDaemon,
} from '../../hooks/queries/useRunnerModels';
import { isActiveDaemonTriggerStatus, useDaemonTriggerModal } from '../../hooks/useDaemonTriggerModal';
import {
  hasPromptContent,
  useDaemonTriggerModalController,
  type OpenDaemonTriggerModalDetail,
} from '../../hooks/useDaemonTriggerModalController';
import { useRunnerDispatchPoliciesWithRecommendation } from '../../hooks/queries/useRunnerDispatchPolicies';
import { useRunnerRequestBootstrap } from '../../hooks/queries/useRunnerRequestBootstrap';
import {
  isRunnerDispatchPolicySelected,
  isRunnerDispatchPolicySelectionPending,
  resolveAutoDispatchPolicySelection,
} from '../../lib/runnerDispatchPolicySelection';
import { useConventions } from '../../hooks/queries/useConventions';
import { clearClientSessionData, getAuthSession, saveAuthSession, subscribeAuthSession } from '../../lib/authSession';
import { getWorkspaceContext, saveWorkspaceContext, type WorkspaceContext } from '../../lib/workspaceContext';
import { readStoredValue, writeStoredValue } from '../../lib/storage';
import { getWorkspaceProjectRoute, saveWorkspaceProjectRoute } from '../../lib/workspaceProjectRoute';
import { touchProject, type AgentConfig, type DaemonTrigger, type SearchResultItem } from '../../lib/api';
import { consentChangedEventName, consentStorageKey, getConsent } from '../../lib/consent';
import { resolveSearchActivityTarget } from '../../lib/searchActivityTarget';
import { getStoredThemeMode, saveThemeMode } from '../ThemeToggle';
import { useSystemThemeSync } from '../../hooks/useSystemThemeSync';
import { useMyProfileQuery, useUpdateMyProfileMutation } from '../../hooks/queries/useMember';
import { ConsentBanner } from '../ConsentBanner';
import AuthGuard from '../AuthGuard';
import ProfileModal from '../ProfileModal';
import WorkspaceModal, { type ModalView } from '../WorkspaceModal';
import { ProjectModal, type ProjectTab } from '../ProjectModal';
import { ExecutionEnvironmentModal } from '../ExecutionEnvironmentModal';
import {
  executionEnvironmentModalEventName,
  parseExecutionEnvironmentSection,
  type ExecutionEnvironmentSection,
} from '../../lib/executionEnvironmentModal';
import { FeedbackSubmitModal } from '../FeedbackSubmitModal';
import { useAppLocale } from '../../providers/AppLocaleProvider';
import { getOnboardingDocsUrl } from '../../lib/docsLinks';
import { PullToRefresh } from '../PullToRefresh';
import { DaemonTriggerModal } from '../DaemonTriggerModal';
import DaemonTriggerDetailModalConnected from '../home/DaemonTriggerDetailModalConnected';
import { prepareUserConventionOptionsForPicker } from '../UserConventionSelector';
import { useUIStore } from '../../stores/uiStore';
import { SearchModal } from '../SearchModal';
import { CommandPaletteTrigger } from './Sidebar/CommandPaletteTrigger';
import { useBillingExposure } from '../../hooks/useBillingExposure';
import { SubscribeModal } from '../billing/SubscribeModal';
import { SubscriptionModal } from '../billing/SubscriptionModal';
import { trackPageView } from '../../lib/analytics';
import { createWebRunnerRequestHost } from '../../lib/webRunnerRequestHost';
import { appTarget } from '../../lib/appTarget';
import { performLogout } from '../../lib/logout';
import { resolveAppHref } from '../../lib/portalRoutes';
import { requestRealtimeReconnect } from '../../lib/realtimeReconnect';
import { getNotificationDetailTargetId, getNotificationTargetReadAction } from '../../lib/notifications/detailTarget';

import { TrialBanner } from '../billing/TrialBanner';
import { type NavItemDef, type NavSectionDef } from './Sidebar';
import {
  CodeReviewsIcon,
  CoActionsIcon,
  CompletionReportsIcon,
  ConfigIcon,
  ConventionsIcon,
  SharedSkillsIcon,
  SkillsIcon,
  DaemonTriggersIcon,
  DocumentsIcon,
  FeedbacksIcon,
  MembersIcon,
  PlansIcon,
  PostMortemsIcon,
  RunnerModelsIcon,
  TeamsIcon,
  MonitoringIcon,
  SearchIndexIcon,
} from './Sidebar/icons';
import { BreadcrumbProvider, HideableTopBar, PageActionsSlotProvider, TopBar } from './TopBar';
import { NotificationBell } from './TopBar/NotificationBell';
import { TopBarUserMenu } from './TopBar/UserMenu';
import {
  getWorkspaceRedirectHref,
  isShelllessRouteForTarget,
  isWorkspaceRouteRememberable,
  shouldSyncWorkspaceQuery,
} from './workspaceRouting';
import { AppShellContextProvider, type AppShellContextValue } from './AppShellContext';
import { shouldHideGlobalSearchProjectSelector } from './searchModalVisibility';
import { shouldLoadDaemonTriggerResources as resolveShouldLoadDaemonTriggerResources } from './daemonTriggerResourceLoading';
import { DaemonFabEntityProvider } from './DaemonFabEntityContext';
import { captureEntryUrl } from '../listFilters/entryUrl';

captureEntryUrl();

const drawerId = 'app-drawer';
const sidebarCollapsedKey = 'agentteams-sidebar-collapsed';
const PROMPT_MAX_LENGTH = 1000;
// 러너 요청 모달이 열려 있는 동안 러너 목록을 주기적으로 갱신하는 간격.
// 러너 관리 모달과 동일하게 잡아, bound 러너의 lastSeenAt이 낡아 온라인인데
// Offline로 오판되는 것을 막는다.
const DAEMON_REQUEST_MODAL_REFETCH_INTERVAL_MS = 30_000;

type DaemonTriggerModalMode = 'create' | 'continue';

type AppProvidersProps = {
  children: ReactNode;
};

export const AppProviders = ({ children }: AppProvidersProps) => {
  const queryClient = useQueryClient();
  const tNav = useTranslations('nav');
  const tCommon = useTranslations('common');
  const tLayout = useTranslations('components.appLayout');
  const tBilling = useTranslations('billing');
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isAuthPage = isShelllessRouteForTarget(pathname, appTarget);
  const isHomeRoute = pathname === '/home';
  const supportsSearchActivityPanel = pathname === '/home' || pathname === '/notifications';

  // SYSTEM 모드에서 OS 테마 변경을 새로고침 없이 전역 반영합니다. (auth 페이지 포함 모든 라우트)
  useSystemThemeSync();

  const [session, setSession] = useState(() => getAuthSession());
  const markTargetNotificationsAsReadMutation = useMarkUnifiedNotificationsForTargetAsReadMutation(
    session?.token ?? null,
  );
  const {
    data: unifiedUnreadCount,
    isSuccess: isUnifiedUnreadCountSuccess,
    isError: isUnifiedUnreadCountError,
  } = useUnifiedUnreadCount(session?.token ?? null);
  const lastReadNotificationTargetRef = useRef<string | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [isWorkspaceModalOpen, setIsWorkspaceModalOpen] = useState(false);
  const [workspaceModalInitialView, setWorkspaceModalInitialView] = useState<ModalView | undefined>(undefined);
  const [projectModalState, setProjectModalState] = useState<{
    projectId: string;
    projectName: string;
    initialTab?: ProjectTab;
  } | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  // null이면 닫힘. 값이 있으면 그 섹션으로 계정 범위 실행 환경 모달을 연다.
  const [executionEnvironmentSection, setExecutionEnvironmentSection] = useState<ExecutionEnvironmentSection | null>(
    null,
  );
  const [daemonTriggerModalMode, setDaemonTriggerModalMode] = useState<DaemonTriggerModalMode>('create');
  const [continueSourceTrigger, setContinueSourceTrigger] = useState<DaemonTrigger | null>(null);
  const [isDaemonTriggerDetailPanelOpen, setIsDaemonTriggerDetailPanelOpen] = useState(false);
  const [isActivityDetailPanelOpen, setIsActivityDetailPanelOpen] = useState(false);
  const [isConsentBannerVisible, setIsConsentBannerVisible] = useState(false);
  const [isPaginationVisible, setIsPaginationVisible] = useState(false);
  const [isFeedbackSubmitModalOpen, setIsFeedbackSubmitModalOpen] = useState(false);
  const { locale } = useAppLocale();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [hideSearchProjectSelector, setHideSearchProjectSelector] = useState(false);
  const hideGlobalSearchProjectSelector = shouldHideGlobalSearchProjectSelector(pathname, hideSearchProjectSelector);
  const [isDaemonTriggerResourceRequested, setIsDaemonTriggerResourceRequested] = useState(false);
  const mainContentRef = useRef<HTMLElement | null>(null);
  const hasTrackedInitialPageViewRef = useRef(false);
  const { data: myProfile } = useMyProfileQuery(session?.token ?? null);

  useEffect(() => {
    const entityId = getNotificationDetailTargetId(pathname, searchParams);
    if (!entityId || !session?.token) {
      lastReadNotificationTargetRef.current = null;
      return;
    }

    const action = getNotificationTargetReadAction({
      entityId,
      isHandled: lastReadNotificationTargetRef.current === entityId,
      unreadCount: unifiedUnreadCount,
      isUnreadCountSuccess: isUnifiedUnreadCountSuccess,
      isUnreadCountError: isUnifiedUnreadCountError,
    });
    if (action === 'WAIT') return;

    lastReadNotificationTargetRef.current = entityId;
    if (action === 'MUTATE_AND_MARK_HANDLED') {
      markTargetNotificationsAsReadMutation.mutate({ entityId });
    }
  }, [
    isUnifiedUnreadCountError,
    isUnifiedUnreadCountSuccess,
    markTargetNotificationsAsReadMutation,
    pathname,
    searchParams,
    session?.token,
    unifiedUnreadCount,
  ]);
  const themeUpdateMutation = useUpdateMyProfileMutation(session?.token ?? '');
  const openSubscribeModal = useUIStore((s) => s.openSubscribeModal);
  const billingExposure = useBillingExposure(
    session?.token ?? null,
    session?.member.tier ?? 'FREE',
    session?.member.isBetaUser ?? false,
  );

  useEffect(() => {
    if (!myProfile?.themeMode) return;
    const currentMode = getStoredThemeMode();
    if (myProfile.themeMode !== currentMode) {
      saveThemeMode(myProfile.themeMode);
    }
  }, [myProfile?.themeMode]);

  useEffect(() => {
    if (!session || !myProfile) {
      return;
    }

    if (
      session.member.email === myProfile.email &&
      session.member.nickname === myProfile.nickname &&
      session.member.tier === myProfile.tier &&
      session.member.locale === myProfile.locale &&
      session.member.isSystemAdmin === myProfile.isSystemAdmin &&
      session.member.isBetaUser === myProfile.isBetaUser &&
      session.member.createdAt === myProfile.createdAt &&
      session.member.updatedAt === myProfile.updatedAt
    ) {
      return;
    }

    saveAuthSession({
      ...session,
      member: {
        id: myProfile.id,
        email: myProfile.email,
        nickname: myProfile.nickname,
        tier: myProfile.tier,
        locale: myProfile.locale,
        isSystemAdmin: myProfile.isSystemAdmin,
        isBetaUser: myProfile.isBetaUser,
        createdAt: myProfile.createdAt,
        updatedAt: myProfile.updatedAt,
      },
    });
  }, [myProfile, session]);

  useEffect(() => {
    const handler = () => setIsDaemonTriggerResourceRequested(true);
    // openDaemonTriggerModal: 모달 오픈 + 리소스 로딩. warmDaemonTriggerResources: 모달은 열지
    // 않고 hover/focus 프리워밍으로 리소스 enabling 쿼리만 켠다.
    window.addEventListener('openDaemonTriggerModal', handler);
    window.addEventListener('warmDaemonTriggerResources', handler);
    return () => {
      window.removeEventListener('openDaemonTriggerModal', handler);
      window.removeEventListener('warmDaemonTriggerResources', handler);
    };
  }, []);

  const hasRunnerRequestParam = searchParams.get('runnerRequest') !== null;
  const shouldLoadDaemonTriggerResources = resolveShouldLoadDaemonTriggerResources({
    pathname,
    isShelllessRoute: isAuthPage,
    hasRunnerRequestParam,
    isDaemonTriggerResourceRequested,
  });

  // 콜드 오픈 시 8개 소스를 단일 요청으로 묶고 각 개별 캐시에 하이드레이트한다(9→1).
  const runnerRequestBootstrap = useRunnerRequestBootstrap(
    session?.token ?? null,
    selectedProjectId,
    session?.member?.id ?? null,
    { enabled: shouldLoadDaemonTriggerResources },
  );
  // 부트스트랩이 정착(성공/실패)하기 전엔 개별 코어 쿼리를 보류해 중복 콜드 페치를 막고,
  // 실패 시엔 개별 쿼리를 폴백으로 활성화한다. 프로젝트 미선택 시(부트스트랩은 프로젝트
  // 스코프라 미실행) 개별 쿼리를 정상 동작시킨다.
  const coreResourcesEnabled =
    shouldLoadDaemonTriggerResources &&
    (selectedProjectId.length === 0 || runnerRequestBootstrap.isSuccess || runnerRequestBootstrap.isError);

  const { data: teams = [], isLoading: isTeamsLoading } = useTeams(session?.token ?? null);
  const {
    data: runnerProjectBundles = [],
    isLoading: isRunnerProjectsLoading,
    isFetching: isRunnerProjectsFetching,
  } = useMeProjects(session?.token ?? null);
  const {
    data: selectedTeamProjects = [],
    isLoading: isProjectsLoading,
    isFetching: isProjectsFetching,
  } = useProjects(session?.token ?? null, selectedTeamId, appTarget === 'portal' ? 'documents.read' : undefined);

  const { data: daemonsResult } = useDaemons(session?.token ?? null, { enabled: coreResourcesEnabled });
  const daemons = useMemo(() => daemonsResult?.daemons ?? [], [daemonsResult?.daemons]);
  const onlineThresholdMs = daemonsResult?.onlineThresholdMs;
  const { data: sharedDaemons = [], isFetching: isSharedDaemonsFetching } = useSharedDaemons(
    session?.token ?? null,
    selectedProjectId,
    { enabled: coreResourcesEnabled },
  );
  const { data: publicRunnerModels } = useRunnerModels({ enabled: coreResourcesEnabled });
  const { data: mergedRunnerModels } = useMergedRunnerModels(session?.token ?? null, session?.member.id ?? null, {
    enabled: coreResourcesEnabled,
  });
  const { data: hydratedRunnerModelCatalogs = {} } = useRunnerModelCatalogs(selectedProjectId);
  const [catalogFallbackDaemonId, setCatalogFallbackDaemonId] = useState('');
  const needsCatalogFallback =
    catalogFallbackDaemonId.length > 0 && !hydratedRunnerModelCatalogs[catalogFallbackDaemonId];
  const { data: fallbackDaemonCatalog } = useRunnerModelsForDaemon(
    session?.token ?? null,
    selectedProjectId,
    catalogFallbackDaemonId,
    { enabled: coreResourcesEnabled && needsCatalogFallback },
  );
  const runnerModelCatalogs = useMemo(() => {
    if (!needsCatalogFallback || !fallbackDaemonCatalog) {
      return hydratedRunnerModelCatalogs;
    }
    return { ...hydratedRunnerModelCatalogs, [catalogFallbackDaemonId]: fallbackDaemonCatalog };
  }, [catalogFallbackDaemonId, fallbackDaemonCatalog, hydratedRunnerModelCatalogs, needsCatalogFallback]);
  const runnerModels = mergedRunnerModels ?? publicRunnerModels;
  const currentMemberId = session?.member?.id ?? null;
  const { data: agentConfigsRaw = [], isFetching: isAgentConfigsFetching } = useAgentConfigs(
    session?.token ?? null,
    selectedProjectId,
    { enabled: coreResourcesEnabled },
  );
  // 자기 프로젝트 AgentConfig: 요청자 본인이 만든 활성 설정만 모달 후보로 사용합니다.
  const ownAgentConfigs = useMemo(() => {
    if (!currentMemberId) return [];
    return agentConfigsRaw.filter((config) => !config.deletedAt && config.createdByMemberId === currentMemberId);
  }, [agentConfigsRaw, currentMemberId]);
  // 공유 러너 소유자가 현재 프로젝트에 제공한 AgentConfig(러너에 바인딩됨)도 후보에 포함합니다.
  // 서버가 공유 레코드의 projectId로 스코프한 민감 정보 없는 안전 projection만 반환합니다.
  const sharedRunnerAgentConfigs = useMemo<AgentConfig[]>(
    () =>
      sharedDaemons.flatMap((daemon) =>
        daemon.agentConfigs.map((config) => ({
          id: config.id,
          name: config.name,
          osType: config.osType ?? undefined,
          targetDaemonId: config.targetDaemonId,
          createdByMemberId: daemon.ownerMemberId,
          createdByName: config.createdByName,
          authPathFolderName: 'shared',
          createdAt: config.createdAt,
          updatedAt: config.createdAt,
          deletedAt: null,
          hasApiKey: true,
          planModeOnly: config.planModeOnly,
          useWorktree: config.useWorktree,
          shared: config.shared ?? true,
          // 이 값이 비면 미러가 그 에이전트를 "폴더 모름"으로 보고 항상 과잉 경고한다.
          runnerCheckout: config.runnerCheckout ?? null,
        })),
      ),
    [sharedDaemons],
  );
  const agentConfigs = useMemo(() => {
    const byId = new Map<string, AgentConfig>();
    for (const config of [...ownAgentConfigs, ...sharedRunnerAgentConfigs]) {
      if (!byId.has(config.id)) {
        byId.set(config.id, config);
      }
    }
    return Array.from(byId.values());
  }, [ownAgentConfigs, sharedRunnerAgentConfigs]);
  const { data: heartbeatData } = useHeartbeat(session?.token ?? null, selectedProjectId, {
    enabled: coreResourcesEnabled,
  });
  const { data: activeDaemonTriggers_ = [], isFetching: isActiveDaemonTriggersFetching } = useActiveDaemonTriggers(
    session?.token ?? null,
    selectedProjectId,
    { enabled: coreResourcesEnabled },
  );
  // 활성 요청은 백엔드에서 공유 러너 가시성을 이미 포함하지만, 클라이언트 캐시가 stale일 때
  // 종료된 트리거가 섞여 conflict 가드를 흔드는 것을 막기 위해 active 상태만 사용합니다.
  const mineActiveDaemonTriggers = useMemo(
    () => activeDaemonTriggers_.filter((trigger) => isActiveDaemonTriggerStatus(trigger.status)),
    [activeDaemonTriggers_],
  );
  // 러너 런타임 주입은 PROJECT scope 컨벤션만 로드·동기화하므로, 요청 모달의 수동 선택 목록도
  // PROJECT로 제한해 "고를 수는 있으나 주입되지 않는" 불일치를 없앤다.
  const { data: projectConventionsForUserPicker = [], isFetching: isProjectConventionsFetching } = useConventions(
    session?.token ?? null,
    selectedProjectId,
    { scope: 'PROJECT', pageSize: 100 },
    { enabled: coreResourcesEnabled },
  );
  const projectConventionOptions = useMemo(
    () =>
      prepareUserConventionOptionsForPicker(
        projectConventionsForUserPicker.map((c) => ({
          id: c.id,
          title: c.title,
          category: c.category,
          contentTokenCount: c.contentTokenCount ?? null,
          runnerInjectedCount: c.runnerInjectedCount,
          trigger: c.trigger,
          updatedAt: c.updatedAt,
        })),
      ),
    [projectConventionsForUserPicker],
  );
  const activeDaemonTriggerCount = heartbeatData?.activeTriggerCount ?? 0;
  const runnableDaemons = useMemo(() => [...daemons, ...sharedDaemons], [daemons, sharedDaemons]);
  const runnerProjectEntries = useMemo(
    () =>
      runnerProjectBundles.flatMap((bundle) =>
        bundle.projects.map((project) => ({
          id: project.id,
          name: project.name,
          teamId: project.teamId,
          teamName: bundle.team.name,
        })),
      ),
    [runnerProjectBundles],
  );
  // 홈 프로젝트 필터가 걸려 있으면 러너 요청 모달의 프로젝트 목록도 해당 프로젝트로 좁힌다.
  // 단, 현재 선택된 프로젝트는 항상 포함해 select 값이 비어버리는 일이 없게 한다.
  const homeFilterProjectIds = useUIStore((state) => state.homeActivityFilters.projects);
  const runnerProjectOptions = useMemo(() => {
    const filterSet = new Set(homeFilterProjectIds);
    const visibleEntries =
      filterSet.size > 0
        ? runnerProjectEntries.filter((project) => filterSet.has(project.id) || project.id === selectedProjectId)
        : runnerProjectEntries;
    return visibleEntries.map((project) => ({
      value: project.id,
      label: project.teamName ? `${project.teamName} / ${project.name}` : project.name,
    }));
  }, [runnerProjectEntries, homeFilterProjectIds, selectedProjectId]);
  const daemonTriggerModal = useDaemonTriggerModal({
    token: session?.token ?? null,
    selectedProjectId,
    enabled: shouldLoadDaemonTriggerResources,
    daemons: runnableDaemons,
    onlineThresholdMs,
    agentConfigs,
    runnerModels,
    runnerModelCatalogs,
    activeDaemonTriggers: mineActiveDaemonTriggers,
    mode: daemonTriggerModalMode,
    continueSourceTrigger,
    promptMaxLength: PROMPT_MAX_LENGTH,
    t: tLayout,
  });
  useEffect(() => {
    setCatalogFallbackDaemonId(daemonTriggerModal.daemonId);
  }, [daemonTriggerModal.daemonId]);
  const isRunnerRequestProjectResourcesLoading =
    shouldLoadDaemonTriggerResources &&
    // 번들 페치 중엔 개별 코어 쿼리가 게이팅(coreResourcesEnabled)돼 isFetching이 false이므로
    // 부트스트랩 페치 상태를 함께 반영해야 스켈레톤/제출 게이트가 정상 동작한다.
    (runnerRequestBootstrap.isFetching ||
      isSharedDaemonsFetching ||
      isAgentConfigsFetching ||
      isActiveDaemonTriggersFetching ||
      isProjectConventionsFetching ||
      daemonTriggerModal.isProjectRepositoriesFetching);
  const daemonTriggerController = useDaemonTriggerModalController({
    token: session?.token ?? null,
    selectedProjectId,
    selectedTeamId,
    isProjectResourcesLoading: isRunnerRequestProjectResourcesLoading,
    daemons: runnableDaemons,
    modalState: daemonTriggerModal,
    mode: daemonTriggerModalMode,
    setMode: setDaemonTriggerModalMode,
    continueSourceTrigger,
    setContinueSourceTrigger,
    t: tLayout,
  });
  // 러너 요청 모달이 열려 있는 동안에만 러너 목록을 폴링해 lastSeenAt을 신선하게 유지한다.
  // 같은 queryKey(['daemons'])를 공유하므로 위 daemons 캐시가 함께 갱신된다.
  useDaemons(session?.token ?? null, {
    enabled: coreResourcesEnabled && daemonTriggerController.isOpen,
    refetchInterval: daemonTriggerController.isOpen ? DAEMON_REQUEST_MODAL_REFETCH_INTERVAL_MS : false,
  });
  // 모달을 열 때는 staleTime(60s)과 무관하게 즉시 한 번 갱신해, 오픈 직후 낡은 캐시로
  // Offline가 잠깐 뜨는 것을 막는다.
  useEffect(() => {
    if (!daemonTriggerController.isOpen) return;
    void queryClient.refetchQueries({ queryKey: ['daemons'], exact: true });
  }, [daemonTriggerController.isOpen, queryClient]);
  const { data: dispatchPolicyData, isFetching: isDispatchPoliciesFetching } =
    useRunnerDispatchPoliciesWithRecommendation(
      session?.token ?? null,
      selectedProjectId,
      daemonTriggerController.dispatchContext
        ? {
            entityType: daemonTriggerController.dispatchContext.entityType,
            entityState: daemonTriggerController.dispatchContext.entityState,
            entityId: daemonTriggerController.dispatchContext.entityId,
          }
        : undefined,
      daemonTriggerController.dispatchContext !== undefined,
    );
  // 정책은 엔티티 컨텍스트가 있을 때만 노출한다. 컨텍스트 없이(FAB로 열기) 이전 조회 데이터가
  // 새어나와 정책 블록이 잘못 뜨는 것을 막는 방어선이다(주 수정은 훅의 keepPreviousData 제거).
  const hasDispatchContext = daemonTriggerController.dispatchContext !== undefined;
  const isRunnerDispatchPolicySelectedForRequest = isRunnerDispatchPolicySelected({
    hasDispatchContext,
    selectedPolicyId: daemonTriggerController.selectedDispatchPolicyId,
  });
  const dispatchPolicies = useMemo(
    () => (hasDispatchContext ? (dispatchPolicyData?.policies ?? []) : []),
    [dispatchPolicyData, hasDispatchContext],
  );
  const dispatchPolicyRecommendation = hasDispatchContext ? (dispatchPolicyData?.recommendation ?? null) : null;
  useEffect(() => {
    if (!daemonTriggerController.dispatchContext) return;
    // AUTO 상태에서만 추천/기본값을 따라가고, 사용자가 직접 고른 유효한 선택은 덮어쓰지 않는다.
    const nextSelection = resolveAutoDispatchPolicySelection({
      selectionSource: daemonTriggerController.dispatchPolicySelectionSource,
      selectedPolicyId: daemonTriggerController.selectedDispatchPolicyId,
      policyIds: dispatchPolicies.map((policy) => policy.id),
      recommendedPolicyId: dispatchPolicyRecommendation?.policyId ?? null,
    });
    if (nextSelection !== null) {
      daemonTriggerController.setSelectedDispatchPolicyId(nextSelection);
    }
  }, [daemonTriggerController, dispatchPolicies, dispatchPolicyRecommendation]);

  useEffect(() => {
    if (!daemonTriggerController.isOpen) {
      setIsDaemonTriggerResourceRequested(false);
    }
  }, [pathname, daemonTriggerController.isOpen]);
  const currentContextQuery = useMemo(
    () => (selectedTeamId && selectedProjectId ? `?teamId=${selectedTeamId}&projectId=${selectedProjectId}` : ''),
    [selectedTeamId, selectedProjectId],
  );
  const handleInternalSearchResultSelect = useCallback(
    ({ item, projectId }: { item: SearchResultItem; projectId: string }) => {
      if (!supportsSearchActivityPanel || projectId.length === 0) {
        return false;
      }

      const target = resolveSearchActivityTarget(item);
      if (!target) {
        return false;
      }

      const project = runnerProjectEntries.find((entry) => entry.id === projectId);
      const params = new URLSearchParams(searchParams.toString());
      params.delete('notification');
      params.delete('runnerRequest');
      params.delete('selected');
      params.set('activity', `${target.entityType}:${target.id}:${projectId}`);
      if (project) {
        params.set('teamId', project.teamId);
        params.set('projectId', project.id);
      }

      const queryString = params.toString();
      router.replace(`${pathname}${queryString.length > 0 ? `?${queryString}` : ''}`, { scroll: false });
      if (project && (project.teamId !== selectedTeamId || project.id !== selectedProjectId)) {
        setSelectedTeamId(project.teamId);
        setSelectedProjectId(project.id);
        void touchProject(session?.token ?? null, project.id);
      }
      return true;
    },
    [
      pathname,
      router,
      runnerProjectEntries,
      searchParams,
      selectedProjectId,
      selectedTeamId,
      session?.token,
      supportsSearchActivityPanel,
    ],
  );
  const handleRunnerRequestProjectChange = useCallback(
    (projectId: string) => {
      const target = runnerProjectEntries.find((project) => project.id === projectId);
      if (!target || target.id === selectedProjectId) {
        return;
      }

      daemonTriggerModal.resetProjectScopedState();
      setIsDaemonTriggerResourceRequested(true);
      setSelectedTeamId(target.teamId);
      setSelectedProjectId(target.id);
      saveWorkspaceContext({
        teamId: target.teamId,
        teamName: target.teamName,
        projectId: target.id,
        projectName: target.name,
      });
      void touchProject(session?.token ?? null, target.id);

      if (!shouldSyncWorkspaceQuery(pathname)) {
        return;
      }

      const params = new URLSearchParams(searchParams.toString());
      params.set('teamId', target.teamId);
      params.set('projectId', target.id);
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [daemonTriggerModal, pathname, router, runnerProjectEntries, searchParams, selectedProjectId, session?.token],
  );
  // 홈 카드의 첫 클릭은 카드에 표시된 프로젝트를 이벤트에 함께 싣는다. 호스트 선택이
  // 아직 비어 있거나 다른 프로젝트면, 유효한 대상일 때만 같은 선택으로 맞춘다.
  // URL 명시 프로젝트는 초기화 effect가 단일 출처로 신뢰하므로 여기서 덮지 않는다:
  // 유효 목록에 없는 projectId는 무시해 기존 선택을 유지한다.
  useEffect(() => {
    if (searchParams.get('projectId')) return;
    const handler = (event: Event) => {
      const projectId = (event as CustomEvent<OpenDaemonTriggerModalDetail>).detail?.projectId;
      if (!projectId || projectId === selectedProjectId) return;
      if (!runnerProjectEntries.some((project) => project.id === projectId)) return;
      handleRunnerRequestProjectChange(projectId);
    };
    window.addEventListener('openDaemonTriggerModal', handler);
    return () => window.removeEventListener('openDaemonTriggerModal', handler);
  }, [handleRunnerRequestProjectChange, runnerProjectEntries, searchParams, selectedProjectId]);
  const runnerRequestHost = createWebRunnerRequestHost(
    {
      session: { token: session?.token ?? null, memberId: session?.member.id ?? null },
      selectedProjectId,
      projects: runnerProjectEntries,
      daemons: runnableDaemons,
      agentConfigs,
      runnerModels,
      activeDaemonTriggers: mineActiveDaemonTriggers,
    },
    {
      currentContextQuery,
      pathname,
      searchParams: searchParams.toString(),
      changeProject: handleRunnerRequestProjectChange,
      replace: (href, options) => router.replace(href, options),
    },
  );

  const searchQueryString = searchParams.toString();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMetaK = (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k';
      if (isMetaK) {
        e.preventDefault();
        setIsSearchModalOpen((previous) => !previous);
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (!isSearchModalOpen) {
      setHideSearchProjectSelector(false);
    }
  }, [isSearchModalOpen]);

  useEffect(() => {
    const path = searchQueryString.length > 0 ? `${pathname}?${searchQueryString}` : pathname;

    if (!hasTrackedInitialPageViewRef.current) {
      hasTrackedInitialPageViewRef.current = true;
      return;
    }

    trackPageView(path);
  }, [pathname, searchQueryString]);

  useEffect(() => {
    const next = getAuthSession();
    setSession((prev) => {
      if (prev?.token === next?.token && prev?.member?.id === next?.member?.id) {
        return prev;
      }
      return next;
    });
  }, [pathname]);

  useEffect(() => {
    const unsubscribe = subscribeAuthSession((next) => {
      setSession((prev) => {
        if (prev?.token === next?.token && prev?.member?.id === next?.member?.id) {
          return prev;
        }
        return next;
      });
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!isWorkspaceRouteRememberable(pathname)) {
      return;
    }

    const routeTeamId = searchParams.get('teamId') ?? '';
    const routeProjectId = searchParams.get('projectId') ?? '';
    if (!routeTeamId || !routeProjectId) {
      return;
    }

    const params = new URLSearchParams(searchParams.toString());
    params.delete('teamId');
    params.delete('projectId');
    const query = params.toString();
    const route = query.length > 0 ? `${pathname}?${query}` : pathname;
    saveWorkspaceProjectRoute(routeProjectId, route);
  }, [pathname, searchParams]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncConsent = () => {
      setIsConsentBannerVisible(getConsent() === null);
    };

    syncConsent();

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== consentStorageKey) {
        return;
      }
      syncConsent();
    };

    window.addEventListener(consentChangedEventName, syncConsent);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(consentChangedEventName, syncConsent);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    // 저장소 차단 환경에서는 펼침 상태로 동작한다 (WEB-R).
    const saved = readStoredValue(sidebarCollapsedKey);
    setIsSidebarCollapsed(saved === '1');
  }, []);

  useEffect(() => {
    const input = document.getElementById(drawerId) as HTMLInputElement | null;
    if (!input) return;
    setIsDrawerOpen(input.checked);
    const handler = () => setIsDrawerOpen(input.checked);
    input.addEventListener('change', handler);
    return () => input.removeEventListener('change', handler);
  }, [pathname]);

  useEffect(() => {
    const input = document.getElementById(drawerId) as HTMLInputElement | null;
    if (!input?.checked) {
      return;
    }

    input.checked = false;
    setIsDrawerOpen(false);
  }, [pathname, searchQueryString]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!isDrawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isAnyVisible = entries.some((entry) => entry.isIntersecting);
        setIsPaginationVisible(isAnyVisible);
      },
      { threshold: 0 },
    );

    const observeTargets = () => {
      const elements = document.querySelectorAll('[data-pagination-controls]');
      if (elements.length === 0) {
        setIsPaginationVisible(false);
        return;
      }
      elements.forEach((el) => observer.observe(el));
    };

    observeTargets();

    const mutationObserver = new MutationObserver(() => {
      observer.disconnect();
      observeTargets();
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  // 내 프로젝트 번들로 projectId→teamId를 해석하기 위한 맵. URL에 projectId만 있고 teamId가
  // 누락된 링크(홈 활동 패널의 일부 네비 등)에서 팀을 복구하는 데 쓴다.
  const projectIdToTeamId = useMemo(() => {
    const map = new Map<string, string>();
    for (const bundle of runnerProjectBundles) {
      for (const project of bundle.projects) {
        map.set(project.id, project.teamId ?? bundle.team.id);
      }
    }
    return map;
  }, [runnerProjectBundles]);
  // 맵을 effect 의존성에 넣으면 번들 재참조마다 effect가 재실행돼 전이 중 렌더 루프를 유발한다.
  // 네비게이션 시점(=searchParams 변경)의 최신 맵만 필요하므로 ref로 읽는다.
  const projectIdToTeamIdRef = useRef(projectIdToTeamId);
  projectIdToTeamIdRef.current = projectIdToTeamId;

  useEffect(() => {
    const teamIdParam = searchParams.get('teamId') ?? '';
    const projectIdParam = searchParams.get('projectId') ?? '';
    // URL이 projectId를 명시하면 그것을 단일 출처로 신뢰한다. teamId가 빠졌으면 프로젝트 맵에서
    // 복구하고, 복구 못 해도 저장된 '다른' 프로젝트로 폴백하지 않는다(잘못된 프로젝트 표시 방지).
    if (projectIdParam) {
      const resolvedTeamId = teamIdParam || projectIdToTeamIdRef.current.get(projectIdParam) || '';
      if (resolvedTeamId) {
        setSelectedTeamId(resolvedTeamId);
      }
      setSelectedProjectId(projectIdParam);
      return;
    }

    const saved = getWorkspaceContext();
    setSelectedTeamId(saved?.teamId ?? '');
    setSelectedProjectId(saved?.projectId ?? '');
  }, [pathname, searchParams]);

  // URL이 projectId만 갖고 teamId가 없을 때, 프로젝트 맵이 (늦게) 로드되면 팀을 보강한다.
  // effect 630은 네비 시점에만 돌므로 콜드 로드/맵 지연을 이 effect가 메운다. resolved와 같아지면 멈춘다.
  useEffect(() => {
    const projectIdParam = searchParams.get('projectId');
    if (!projectIdParam || searchParams.get('teamId')) return;
    const resolved = projectIdToTeamId.get(projectIdParam);
    if (resolved && resolved !== selectedTeamId) {
      setSelectedTeamId(resolved);
    }
  }, [searchParams, projectIdToTeamId, selectedTeamId]);

  useEffect(() => {
    if (teams.length === 0) {
      return;
    }

    const isCurrentTeamValid = teams.some((team) => team.id === selectedTeamId);
    if (isCurrentTeamValid) {
      return;
    }

    const saved = getWorkspaceContext();
    const nextTeamId = saved && teams.some((team) => team.id === saved.teamId) ? saved.teamId : (teams[0]?.id ?? '');

    if (nextTeamId.length > 0 && nextTeamId !== selectedTeamId) {
      setSelectedTeamId(nextTeamId);
    }
  }, [teams, selectedTeamId]);

  useEffect(() => {
    // URL이 명시한 projectId는 (선택 팀이 아직 안 맞아 검증에 실패해도) 비우지 않는다.
    // team resolver가 팀을 맞출 때까지 기다려, 저장된 '다른' 프로젝트로 되돌아가는 것을 막는다.
    const urlProjectId = searchParams.get('projectId') ?? '';
    const canClear = (id: string) => id.length > 0 && id !== urlProjectId;

    if (!selectedTeamId) {
      if (canClear(selectedProjectId)) {
        setSelectedProjectId('');
      }
      return;
    }

    const teamProjects = selectedTeamProjects.filter((project) => project.teamId === selectedTeamId);
    if (isProjectsLoading || isProjectsFetching) {
      return;
    }
    if (teamProjects.length === 0) {
      // 선택 팀에 프로젝트가 없으면(첫 팀이 비어 있는 경우 등) 접근 가능한 다른 팀의
      // 첫 프로젝트로 대체한다. URL 명시 프로젝트는 비우지도 대체하지도 않는다.
      if (!searchParams.get('projectId')) {
        const fallback = runnerProjectEntries[0];
        if (fallback) {
          if (fallback.id !== selectedProjectId || fallback.teamId !== selectedTeamId) {
            setSelectedTeamId(fallback.teamId);
            setSelectedProjectId(fallback.id);
          }
          return;
        }
      }
      if (canClear(selectedProjectId)) {
        setSelectedProjectId('');
      }
      return;
    }

    const isCurrentProjectValid = teamProjects.some((project) => project.id === selectedProjectId);
    if (isCurrentProjectValid) {
      return;
    }

    if (selectedProjectId) {
      if (canClear(selectedProjectId)) {
        setSelectedProjectId('');
      }
      return;
    }

    const saved = getWorkspaceContext();
    const nextProjectId =
      saved && saved.teamId === selectedTeamId && teamProjects.some((project) => project.id === saved.projectId)
        ? saved.projectId
        : teamProjects[0].id;

    if (nextProjectId !== selectedProjectId) {
      setSelectedProjectId(nextProjectId);
    }
  }, [
    selectedTeamId,
    selectedProjectId,
    selectedTeamProjects,
    isProjectsLoading,
    isProjectsFetching,
    searchParams,
    runnerProjectEntries,
  ]);

  const isAdminMode =
    (pathname.startsWith('/system') || pathname === '/feedbacks') && session?.member.isSystemAdmin === true;

  // 내비게이션은 현재 프로젝트의 유효 permission을 따른다. 서버가 최종 경계이고 이건 안내용이다.
  const {
    can: canProjectFeature,
    isLoading: isProjectAccessLoading,
    permissions: projectPermissions,
  } = useProjectPermissions({
    projectId: selectedProjectId,
  });
  const { data: navigationBadges, isError: isNavigationBadgesError } = useNavigationBadges({
    token: session?.token ?? null,
    projectId: selectedProjectId,
    memberId: session?.member.id ?? null,
    permissions: projectPermissions,
    isAccessReady: !isProjectAccessLoading,
    enabled: !isAuthPage && !isAdminMode,
  });
  const navSections = useMemo<NavSectionDef[]>(() => {
    const statusBadge = (
      field: keyof NonNullable<typeof navigationBadges>,
      severity: 'neutral' | 'info' | 'warning',
    ) => {
      const count = navigationBadges?.[field];
      if (count === null) return undefined;
      return {
        count,
        severity,
        description:
          typeof count === 'number'
            ? tNav(`badges.${field}`, { count })
            : tNav(isNavigationBadgesError ? 'badges.unavailable' : 'badges.loading'),
      };
    };
    if (isAdminMode) {
      const adminSection: NavSectionDef = {
        items: [
          {
            label: tNav('monitoring'),
            href: `/system/monitoring${currentContextQuery}`,
            depth: 0,
            icon: <MonitoringIcon />,
          },
          {
            label: tNav('searchIndex'),
            href: `/system/search${currentContextQuery}`,
            depth: 0,
            icon: <SearchIndexIcon />,
          },
          {
            label: tNav('runnerModels'),
            href: `/system/runner-models${currentContextQuery}`,
            depth: 0,
            icon: <RunnerModelsIcon />,
          },
          {
            label: tNav('config'),
            href: `/system/config${currentContextQuery}`,
            depth: 0,
            icon: <ConfigIcon />,
          },
          {
            label: tNav('members'),
            href: `/system/members${currentContextQuery}`,
            depth: 0,
            icon: <MembersIcon />,
          },
          {
            label: tNav('teams'),
            href: `/system/teams${currentContextQuery}`,
            depth: 0,
            icon: <TeamsIcon />,
          },
          {
            label: tNav('feedbacks'),
            href: `/feedbacks${currentContextQuery}`,
            depth: 0,
            icon: <FeedbacksIcon />,
          },
        ],
      };
      return [adminSection];
    }

    // 액션별 그룹: 작업(실행 구동) / 산출물(실행 결과) / 기준(규칙·지식·설정).
    // 권한 없는 메뉴는 비활성 링크로 남기지 않고 아예 렌더하지 않는다. 남겨두면 직접 접근을
    // 유도하고, 서버가 403으로 막는 화면을 굳이 안내하는 셈이 된다.
    // permission을 아직 모르는 동안에도 감춘다 — 잠깐 보였다 사라지면 그것도 노출이다.
    // href는 `PROJECT_NAV_ROUTES`(SSOT)에서 가져온다. permission↔path 매핑을 여기 두면
    // 랜딩 결정(workspaceRouting)과 두 벌이 되어 메뉴 순서와 목적지가 어긋난다.
    const gate = (permission: ProjectNavPermission, item: Omit<NavItemDef, 'href'>): NavItemDef[] =>
      canProjectFeature(permission)
        ? [{ ...item, href: `${getProjectNavPath(permission)}${currentContextQuery}` }]
        : [];

    const workItems: NavItemDef[] = [
      ...gate('plans.read', {
        label: tNav('plans'),
        statusBadge: statusBadge('plans', 'neutral'),
        depth: 0,
        icon: <PlansIcon />,
      }),
      ...gate('runners.read', {
        label: tNav('daemonTriggers'),
        statusBadge: statusBadge('runners', 'info'),
        depth: 0,
        icon: <DaemonTriggersIcon />,
      }),
      ...gate('coActions.read', {
        label: tNav('coActions'),
        statusBadge: statusBadge('coActions', 'neutral'),
        depth: 0,
        icon: <CoActionsIcon />,
      }),
    ];

    const recordItems: NavItemDef[] = [
      ...gate('reports.read', {
        label: tNav('completionReports'),
        statusBadge: statusBadge('completionReports', 'warning'),
        depth: 0,
        icon: <CompletionReportsIcon />,
      }),
      ...gate('codeReviews.read', {
        label: tNav('codeReviews'),
        statusBadge: statusBadge('codeReviews', 'info'),
        depth: 0,
        icon: <CodeReviewsIcon />,
      }),
      ...gate('postMortems.read', {
        label: tNav('postMortems'),
        statusBadge: statusBadge('postMortems', 'warning'),
        depth: 0,
        icon: <PostMortemsIcon />,
      }),
    ];

    const standardItems: NavItemDef[] = [
      ...gate('conventions.read', {
        label: tNav('conventions'),
        depth: 0,
        icon: <ConventionsIcon />,
      }),
      ...gate('skills.read', {
        label: tNav('skills'),
        depth: 0,
        icon: <SkillsIcon />,
      }),
      // 공유 탐색은 소비 프로젝트 문맥이 필요하지만 PROJECT_NAV_ROUTES에는 두지 않는다 —
      // SSOT에 넣으면 랜딩 우선순위까지 바뀌고, 상세(`/skills/detail`)처럼 내비 라우트가
      // 아닌 경로는 workspaceRouting이 그대로 통과시키므로 별도 항목으로 둔다.
      ...(canProjectFeature('skills.read')
        ? [
            {
              label: tNav('sharedSkills'),
              depth: 0,
              icon: <SharedSkillsIcon />,
              href: `/skills/shared${currentContextQuery}`,
            } satisfies NavItemDef,
          ]
        : []),
      ...gate('documents.read', {
        label: tNav('documents'),
        depth: 0,
        icon: <DocumentsIcon />,
      }),
      ...gate('agents.read', {
        label: tNav('agentConfigs'),
        depth: 0,
        icon: <Bot className="h-5 w-5" aria-hidden="true" />,
      }),
    ];

    const sections: NavSectionDef[] = [
      {
        items: gate('dashboard.read', {
          label: tNav('dashboard'),
          depth: 0,
          icon: <LayoutDashboard className="h-5 w-5" aria-hidden="true" />,
        }),
      },
      { label: tNav('groupWork'), items: workItems },
      { label: tNav('groupRecords'), items: recordItems },
      { label: tNav('groupStandards'), items: standardItems },
    ];

    // 항목이 하나도 없는 그룹은 제목만 남으므로 통째로 제거한다.
    return sections.filter((section) => section.items.length > 0);
  }, [tNav, currentContextQuery, isAdminMode, canProjectFeature, navigationBadges, isNavigationBadgesError]);

  const lastSyncedRef = useRef<{ teamId: string; projectId: string; accessResolved: boolean } | null>(null);
  useEffect(() => {
    if (!selectedTeamId) return;
    const team = teams.find((item) => item.id === selectedTeamId);
    if (!team) return;

    const project = selectedProjectId
      ? (selectedTeamProjects.find((item) => item.id === selectedProjectId) ?? null)
      : null;
    if (selectedProjectId && !project) return;

    // 권한 판정이 끝나는 순간 리다이렉트 목적지를 다시 계산해야 하므로, 판정 완료 여부도 동기화 키에 넣는다.
    // (컨텍스트 저장·touchProject는 아래 내용 비교로 한 번만 수행된다.)
    const accessResolved = !isProjectAccessLoading;
    const synced = lastSyncedRef.current;
    if (
      synced &&
      synced.teamId === selectedTeamId &&
      synced.projectId === (project?.id ?? '') &&
      synced.accessResolved === accessResolved
    ) {
      return;
    }
    lastSyncedRef.current = { teamId: selectedTeamId, projectId: project?.id ?? '', accessResolved };

    const context: WorkspaceContext = {
      teamId: team.id,
      teamName: team.name,
      projectId: project?.id ?? '',
      projectName: project?.name ?? '',
    };
    const current = getWorkspaceContext();
    if (
      current?.teamId === context.teamId &&
      current?.projectId === context.projectId &&
      current?.teamName === context.teamName &&
      current?.projectName === context.projectName
    ) {
      if (!shouldSyncWorkspaceQuery(pathname)) return;
      const currentTeamId = searchParams.get('teamId') ?? '';
      const currentProjectId = searchParams.get('projectId') ?? '';
      if (currentTeamId === context.teamId && currentProjectId === context.projectId) return;
    } else {
      saveWorkspaceContext(context);
      void touchProject(session?.token ?? null, context.projectId);
    }

    if (!shouldSyncWorkspaceQuery(pathname)) return;

    const currentTeamId = searchParams.get('teamId') ?? '';
    const currentProjectId = searchParams.get('projectId') ?? '';
    const preferredRoute = getWorkspaceProjectRoute(context.projectId);
    const href = getWorkspaceRedirectHref({
      pathname,
      searchParams: new URLSearchParams(searchParams.toString()),
      preferredRoute,
      nextTeamId: context.teamId,
      nextProjectId: context.projectId,
      currentTeamId,
      currentProjectId,
      access: { isLoading: isProjectAccessLoading, can: canProjectFeature },
    });
    if (!href) return;
    router.replace(resolveAppHref(href));
  }, [
    selectedTeamId,
    selectedProjectId,
    teams,
    selectedTeamProjects,
    pathname,
    router,
    searchParams,
    session?.token,
    isProjectAccessLoading,
    canProjectFeature,
  ]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ initialView?: ModalView }>).detail;
      setWorkspaceModalInitialView(detail?.initialView);
      setIsWorkspaceModalOpen(true);
    };
    window.addEventListener('openWorkspaceModal', handler);
    return () => window.removeEventListener('openWorkspaceModal', handler);
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ projectId: string; projectName: string; initialTab?: ProjectTab }>).detail;
      if (detail?.projectId) {
        setProjectModalState({
          projectId: detail.projectId,
          projectName: detail.projectName,
          initialTab: detail.initialTab,
        });
      }
    };
    window.addEventListener('openProjectModal', handler);
    return () => window.removeEventListener('openProjectModal', handler);
  }, []);

  // 러너·개인 모델은 계정 자산이라 프로젝트 화면 안에 페이지로 두지 않고 어느 화면에서든 전역 모달로 연다.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ section?: string } | undefined>).detail;
      setExecutionEnvironmentSection(parseExecutionEnvironmentSection(detail?.section));
    };
    window.addEventListener(executionEnvironmentModalEventName, handler);
    return () => window.removeEventListener(executionEnvironmentModalEventName, handler);
  }, []);

  const handleLogout = useCallback(async () => {
    await performLogout();
  }, []);

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarCollapsed((previous) => {
      const next = !previous;
      writeStoredValue(sidebarCollapsedKey, next ? '1' : '0');
      return next;
    });
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ isOpen?: boolean }>;
      setIsDaemonTriggerDetailPanelOpen(Boolean(customEvent.detail?.isOpen));
    };
    window.addEventListener('daemonTriggerDetailPanelVisibilityChanged', handler);
    return () => window.removeEventListener('daemonTriggerDetailPanelVisibilityChanged', handler);
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ isOpen?: boolean }>;
      setIsActivityDetailPanelOpen(Boolean(customEvent.detail?.isOpen));
    };
    window.addEventListener('activityDetailPanelVisibilityChanged', handler);
    return () => window.removeEventListener('activityDetailPanelVisibilityChanged', handler);
  }, []);

  const handlePullToRefresh = useCallback(async () => {
    requestRealtimeReconnect('pull-to-refresh');
    await queryClient.invalidateQueries();
  }, [queryClient]);

  const handleOpenSearch = useCallback(() => {
    setIsSearchModalOpen(true);
  }, []);

  const handleOpenSearchFromCommandPalette = useCallback(() => {
    setHideSearchProjectSelector(true);
    setIsSearchModalOpen(true);
    (document.activeElement as HTMLElement | null)?.blur();
  }, []);

  const handleOpenProfile = useCallback(() => {
    setIsProfileModalOpen(true);
    (document.activeElement as HTMLElement | null)?.blur();
  }, []);

  const handleOpenSubscribe = useCallback(() => {
    (document.activeElement as HTMLElement | null)?.blur();
    openSubscribeModal();
  }, [openSubscribeModal]);

  const handleOpenSubscription = useCallback(() => {
    (document.activeElement as HTMLElement | null)?.blur();
    useUIStore.getState().openSubscriptionModal();
  }, []);

  // 러너·개인 모델은 계정 범위 자산이므로 프로젝트 화면에 종속돼 보이지 않게 전역 모달로 연다.
  const handleOpenExecutionEnvironment = useCallback(() => {
    (document.activeElement as HTMLElement | null)?.blur();
    setExecutionEnvironmentSection('runners');
  }, []);

  const handleOpenOnboardingGuideFromMenu = useCallback(() => {
    (document.activeElement as HTMLElement | null)?.blur();
    window.open(getOnboardingDocsUrl(locale), '_blank', 'noopener,noreferrer');
  }, [locale]);

  const handleOpenFeedback = useCallback(() => {
    setIsFeedbackSubmitModalOpen(true);
    (document.activeElement as HTMLElement | null)?.blur();
  }, []);

  const handleSelectThemeMode = useCallback(
    (mode: ReturnType<typeof getStoredThemeMode>) => {
      saveThemeMode(mode);
      if (session?.token) {
        void themeUpdateMutation.mutateAsync({ themeMode: mode }).catch(() => {});
      }
    },
    [session?.token, themeUpdateMutation],
  );

  const currentTeam = teams.find((t) => t.id === selectedTeamId);
  const currentProject = selectedTeamProjects.find((p) => p.id === selectedProjectId);
  const isProjectsBusy = selectedTeamId.length > 0 && (isProjectsLoading || isProjectsFetching);
  const isProjectsLoadingView = selectedTeamId.length > 0 && isProjectsBusy;
  // 아직 "어느 프로젝트인지"가 정해지지 않았고, 곧 정해질 근거가 남아 있는 구간.
  // 이 신호가 없으면 프로젝트 게이트가 이 왕복 동안 "권한 없음"으로 결론을 내고 거부 화면을 선렌더한다.
  // 팀이 0개거나 선택 팀의 프로젝트가 0개로 확정되면 false가 되므로 무한 로딩으로 남지 않는다.
  const isWorkspaceProjectResolving =
    !isAdminMode &&
    selectedProjectId.length === 0 &&
    (isTeamsLoading ||
      isProjectsBusy ||
      (selectedTeamId.length === 0 && teams.length > 0) ||
      selectedTeamProjects.some((project) => project.teamId === selectedTeamId));
  // permission을 아직 모르는 동안 gate()는 모든 항목을 감춘다. 그대로 두면 "메뉴 없음"이라는
  // 결론을 먼저 보여주는 셈이라, 판정 전에는 스켈레톤으로 자리만 잡는다(관리자 콘솔은 권한 무관).
  const isNavLoadingView = !isAdminMode && isProjectAccessLoading;

  const contextValue = useMemo<AppShellContextValue>(
    () => ({
      session,
      selectedTeamId,
      selectedProjectId,
      currentTeam,
      currentProject,
      currentContextQuery,
      isProjectsLoadingView,
      isWorkspaceProjectResolving,
      drawerId,
      isDrawerOpen,
      isSidebarCollapsed,
      onToggleSidebar: handleSidebarToggle,
      mainContentRef,
      onPullToRefresh: handlePullToRefresh,
      billingExposure,
      navSections,
      isNavLoadingView,
      isAdminMode,
      runnableDaemons,
      daemonTriggerModal,
      daemonTriggerController,
      mineActiveDaemonTriggers,
      activeDaemonTriggerCount,
      isDaemonTriggerDetailPanelOpen,
      isActivityDetailPanelOpen,
      isConsentBannerVisible,
      isPaginationVisible,
      onLogout: handleLogout,
      onOpenSearch: handleOpenSearch,
      onOpenSearchFromCommandPalette: handleOpenSearchFromCommandPalette,
      onOpenProfile: handleOpenProfile,
      onOpenSubscribe: handleOpenSubscribe,
      onOpenSubscription: handleOpenSubscription,
      onOpenExecutionEnvironment: handleOpenExecutionEnvironment,
      onOpenOnboardingGuideFromMenu: handleOpenOnboardingGuideFromMenu,
      onOpenFeedback: handleOpenFeedback,
      onSelectThemeMode: handleSelectThemeMode,
      tNav,
      tLayout,
      tCommon,
      tBilling,
    }),
    [
      session,
      selectedTeamId,
      selectedProjectId,
      currentTeam,
      currentProject,
      currentContextQuery,
      isProjectsLoadingView,
      isWorkspaceProjectResolving,
      isDrawerOpen,
      isSidebarCollapsed,
      handleSidebarToggle,
      handlePullToRefresh,
      billingExposure,
      navSections,
      isNavLoadingView,
      isAdminMode,
      runnableDaemons,
      daemonTriggerModal,
      daemonTriggerController,
      mineActiveDaemonTriggers,
      activeDaemonTriggerCount,
      isDaemonTriggerDetailPanelOpen,
      isActivityDetailPanelOpen,
      isConsentBannerVisible,
      isPaginationVisible,
      handleLogout,
      handleOpenSearch,
      handleOpenSearchFromCommandPalette,
      handleOpenProfile,
      handleOpenSubscribe,
      handleOpenSubscription,
      handleOpenExecutionEnvironment,
      handleOpenOnboardingGuideFromMenu,
      handleOpenFeedback,
      handleSelectThemeMode,
      tNav,
      tLayout,
      tCommon,
      tBilling,
    ],
  );

  if (isAuthPage) {
    return <div className="min-h-screen">{children}</div>;
  }

  const homeUserMenu = (
    <TopBarUserMenu
      session={session}
      billingExposure={billingExposure}
      tNav={tNav}
      tCommon={tCommon}
      tBilling={tBilling}
      themeMode={getStoredThemeMode()}
      onSelectThemeMode={handleSelectThemeMode}
      onOpenProfile={handleOpenProfile}
      onOpenSubscribeModal={handleOpenSubscribe}
      onOpenSubscriptionModal={handleOpenSubscription}
      onOpenExecutionEnvironment={handleOpenExecutionEnvironment}
      onOpenOnboardingGuide={handleOpenOnboardingGuideFromMenu}
      onOpenFeedback={handleOpenFeedback}
      onOpenAdminMenu={() => {
        (document.activeElement as HTMLElement | null)?.blur();
        router.push(resolveAppHref('/system/monitoring'));
      }}
      onLogout={handleLogout}
    />
  );

  const homeChrome = (
    <div className="drawer">
      <input id={drawerId} type="checkbox" className="drawer-toggle" />
      <div className="drawer-content flex flex-col lg:h-screen lg:overflow-hidden">
        <HideableTopBar>
          <TopBar
            drawerId={drawerId}
            isDrawerOpen={isDrawerOpen}
            tLayout={tLayout}
            onOpenSearch={handleOpenSearch}
            searchOnlyOnMobile
            desktopSearchTrigger={
              <CommandPaletteTrigger
                collapsed={false}
                onOpen={() => {
                  setIsSearchModalOpen(true);
                  (document.activeElement as HTMLElement | null)?.blur();
                }}
                t={tLayout}
              />
            }
            notificationBell={<NotificationBell token={session?.token ?? null} />}
            userMenu={homeUserMenu}
            hideMenuToggle
            showLogo
            showHomeButton={false}
          />
        </HideableTopBar>
        <PullToRefresh onRefresh={handlePullToRefresh} scrollRef={mainContentRef}>
          <main
            ref={mainContentRef}
            className="min-h-0 min-w-0 flex-1 overflow-x-hidden p-4 md:p-6 lg:overflow-y-auto lg:p-8"
          >
            <TrialBanner show={billingExposure.showTrialBanner} daysLeft={billingExposure.trialDaysLeft} />
            {children}
          </main>
        </PullToRefresh>
      </div>
    </div>
  );

  return (
    <AuthGuard>
      <BreadcrumbProvider>
        <PageActionsSlotProvider>
          <AppShellContextProvider value={contextValue}>
            <DaemonFabEntityProvider>{isHomeRoute ? homeChrome : children}</DaemonFabEntityProvider>

            <WorkspaceModal
              isOpen={isWorkspaceModalOpen}
              onClose={() => {
                setIsWorkspaceModalOpen(false);
                setWorkspaceModalInitialView(undefined);
              }}
              initialView={workspaceModalInitialView}
            />
            {projectModalState ? (
              <ProjectModal
                isOpen
                onClose={() => setProjectModalState(null)}
                projectId={projectModalState.projectId}
                projectName={projectModalState.projectName}
                initialTab={projectModalState.initialTab}
                onProjectDeleted={() => {
                  // 프로젝트 화면에서 현재 프로젝트를 삭제하면 모달을 닫고 홈으로 이동한다.
                  setProjectModalState(null);
                  router.push(resolveAppHref('/home'));
                }}
              />
            ) : null}
            <ProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} />
            <ExecutionEnvironmentModal
              isOpen={executionEnvironmentSection !== null}
              initialSection={executionEnvironmentSection ?? 'runners'}
              onClose={() => setExecutionEnvironmentSection(null)}
            />
            <SearchModal
              isOpen={isSearchModalOpen}
              onClose={() => {
                setIsSearchModalOpen(false);
                setHideSearchProjectSelector(false);
              }}
              token={session?.token ?? null}
              projectId={selectedProjectId}
              currentContextQuery={currentContextQuery}
              hideProjectSelector={hideGlobalSearchProjectSelector}
              onInternalResultSelect={supportsSearchActivityPanel ? handleInternalSearchResultSelect : undefined}
            />
            <FeedbackSubmitModal
              isOpen={isFeedbackSubmitModalOpen}
              token={session?.token ?? null}
              onCloseAction={() => setIsFeedbackSubmitModalOpen(false)}
            />
            <DaemonTriggerModal
              isOpen={daemonTriggerController.isOpen}
              mode={daemonTriggerController.mode}
              isMuted={searchParams.get('runnerRequest') !== null}
              requestsHref={runnerRequestHost.navigation.requestsHref}
              state={{
                selectedProjectId: runnerRequestHost.selectedProjectId,
                canChangeProject: isHomeRoute,
                isProjectSelectionLoading: isRunnerProjectsLoading || isRunnerProjectsFetching,
                daemonPlanMode: daemonTriggerModal.daemonPlanMode,
                worktree: {
                  daemonUseWorktree: daemonTriggerModal.daemonUseWorktree,
                  worktreeSelectionMode: daemonTriggerModal.worktreeSelectionMode,
                  selectedExistingWorktreeId: daemonTriggerModal.selectedExistingWorktreeId,
                  selectedExistingRepositoryId: daemonTriggerModal.selectedExistingRepositoryId,
                  daemonRepositoryId: daemonTriggerModal.daemonRepositoryId,
                  daemonBaseBranch: daemonTriggerModal.daemonBaseBranch,
                  branchInputMode: daemonTriggerModal.branchInputMode,
                  branchInputHelpMessage: daemonTriggerModal.branchInputHelpMessage,
                  showProjectRepositorySettingsCta: daemonTriggerModal.showProjectRepositorySettingsCta,
                  worktreeWarningMessage: daemonTriggerModal.worktreeWarningMessage,
                  isWorktreeUsageDisabled: daemonTriggerModal.isWorktreeUsageDisabled,
                  isRepositoryBranchesFetching: daemonTriggerModal.isRepositoryBranchesFetching,
                  isActiveWorktreesFetching: daemonTriggerModal.isActiveWorktreesFetching,
                  isActiveWorktreesError: daemonTriggerModal.isActiveWorktreesError,
                  isExistingModeDisabled: daemonTriggerModal.daemonAgentConfigId.length === 0,
                },
                isActiveRequestsSummaryExpanded: daemonTriggerModal.isActiveRequestsSummaryExpanded,
                daemonId: daemonTriggerModal.daemonId,
                daemonAgentConfigId: daemonTriggerModal.daemonAgentConfigId,
                daemonRunnerType: daemonTriggerModal.daemonRunnerType,
                daemonModel: daemonTriggerModal.daemonModelOptionValue,
                daemonEffort: daemonTriggerModal.daemonEffort,
                isSelectedDaemonPlanModeOnly: daemonTriggerModal.isSelectedDaemonPlanModeOnly,
                isPlanModeForced: daemonTriggerModal.isPlanModeForced,
                daemonPrompt: daemonTriggerModal.daemonPrompt,
                promptTextLength: daemonTriggerModal.promptTextLength,
                promptMaxLength: PROMPT_MAX_LENGTH,
                isPromptOverLimit: daemonTriggerModal.isPromptOverLimit,
                activeConcurrencyWarningReason: daemonTriggerModal.activeConcurrencyWarningReason,
                daemonConflictSelectButtonClassName: daemonTriggerModal.daemonConflictSelectButtonClassName,
                isBoundDaemonOffline: daemonTriggerModal.isBoundDaemonOffline,
                resolvedSettings: daemonTriggerModal.resolvedSettings,
                isResolvedSettingsError: daemonTriggerModal.isResolvedSettingsError,
                hasConnectedDaemon: daemonTriggerModal.hasConnectedDaemon,
                selectableDaemonCount: daemonTriggerModal.selectableDaemonCount,
                isInitialLoading: daemonTriggerController.isCheckingAvailability,
                isSubmitPending: daemonTriggerController.isSubmitPending,
                // 오픈 직후 가용성 refetch가 끝나기 전에는 낡은 충돌 데이터 기준 제출을 막는다.
                isSubmitDisabled:
                  daemonTriggerModal.isSubmitDisabled ||
                  (!hasPromptContent(daemonTriggerModal.daemonPrompt ?? {}) &&
                    !isRunnerDispatchPolicySelectedForRequest) ||
                  isRunnerRequestProjectResourcesLoading ||
                  daemonTriggerController.isSubmitPending ||
                  daemonTriggerController.isCheckingAvailability ||
                  isRunnerDispatchPolicySelectionPending({
                    hasDispatchContext: daemonTriggerController.dispatchContext !== undefined,
                    isFetching: isDispatchPoliciesFetching,
                    policyCount: dispatchPolicies.length,
                    selectedPolicyId: daemonTriggerController.selectedDispatchPolicyId,
                  }),
                selectedUserConventionIds: daemonTriggerModal.selectedUserConventionIds,
                attachmentDrafts: daemonTriggerController.attachmentDrafts,
                dispatchPolicy: {
                  dispatchContext: daemonTriggerController.dispatchContext,
                  selectedDispatchPolicyId: daemonTriggerController.selectedDispatchPolicyId,
                  dispatchPolicies,
                  dispatchPolicyRecommendation,
                },
              }}
              options={{
                projectOptions: runnerProjectOptions,
                activeDaemonTriggers: runnerRequestHost.activeDaemonTriggers,
                agentConfigNameById: Object.fromEntries(
                  runnerRequestHost.agentConfigs.map((config) => [config.id, config.name]),
                ),
                daemonOptions: daemonTriggerModal.daemonOptions,
                daemonAgentConfigOptions: daemonTriggerModal.daemonAgentConfigOptions,
                daemonRunnerTypeOptions: daemonTriggerModal.daemonRunnerTypeOptions,
                daemonModelOptions: daemonTriggerModal.daemonModelOptions,
                daemonEffortOptions: daemonTriggerModal.daemonEffortOptions,
                showEffortSelect: daemonTriggerModal.showEffortSelect,
                worktree: {
                  daemonRepositoryOptions: daemonTriggerModal.daemonRepositoryOptions,
                  daemonBranchOptions: daemonTriggerModal.daemonBranchOptions,
                  existingWorktreeOptions: daemonTriggerModal.existingWorktreeOptions,
                  existingRepositoryOptions: daemonTriggerModal.existingRepositoryOptions,
                },
                userConventionOptions: projectConventionOptions,
              }}
              actions={{
                onClose: daemonTriggerController.handleClose,
                onProjectChange: runnerRequestHost.navigation.changeProject,
                worktree: {
                  onDismissWorktreeWarning: () => daemonTriggerModal.setWorktreeWarningMessage(null),
                  onDaemonUseWorktreeChange: daemonTriggerModal.handleDaemonUseWorktreeChange,
                  onWorktreeSelectionModeChange: daemonTriggerModal.handleWorktreeSelectionModeChange,
                  onExistingWorktreeChange: daemonTriggerModal.handleExistingWorktreeChange,
                  onExistingRepositoryChange: daemonTriggerModal.onExistingRepositoryChange,
                  onDaemonRepositoryIdChange: daemonTriggerModal.handleDaemonRepositoryChange,
                  onDaemonBaseBranchChange: daemonTriggerModal.setDaemonBaseBranch,
                  onOpenProjectRepositorySettings: runnerRequestHost.navigation.openRepositorySettings,
                },
                onToggleActiveRequestsSummary: () =>
                  daemonTriggerModal.setIsActiveRequestsSummaryExpanded((prev) => !prev),
                onDaemonIdChange: daemonTriggerModal.handleDaemonIdChange,
                onDaemonAgentConfigIdChange: daemonTriggerModal.handleDaemonAgentConfigIdChange,
                onDaemonRunnerTypeChange: daemonTriggerModal.handleDaemonRunnerTypeChange,
                onDaemonModelChange: daemonTriggerModal.handleDaemonModelChange,
                onDaemonEffortChange: daemonTriggerModal.setDaemonEffort,
                onDaemonPlanModeChange: (checked) => {
                  if (!daemonTriggerModal.isPlanModeForced) {
                    daemonTriggerModal.setDaemonPlanMode(checked);
                  }
                },
                onDaemonPromptChange: daemonTriggerModal.setDaemonPrompt,
                onToggleUserConventionId: daemonTriggerModal.toggleUserConventionId,
                onUserConventionIdsChange: daemonTriggerModal.setSelectedUserConventionIds,
                onAttachmentDraftsChange: daemonTriggerController.setAttachmentDrafts,
                dispatchPolicy: {
                  onDispatchPolicyChange: daemonTriggerController.selectDispatchPolicyAsUser,
                  onRestoreRecommendedPolicy: dispatchPolicyRecommendation
                    ? () =>
                        daemonTriggerController.restoreRecommendedDispatchPolicy(dispatchPolicyRecommendation.policyId)
                    : undefined,
                },
                onViewSourceTrigger: daemonTriggerController.continueSourceTrigger
                  ? () => {
                      const source = daemonTriggerController.continueSourceTrigger;
                      if (!source) return;
                      runnerRequestHost.navigation.viewSourceTrigger(source);
                    }
                  : undefined,
                onSubmit: () => void daemonTriggerController.handleSubmit(),
              }}
              preset={{
                token: runnerRequestHost.session.token,
                projectId: selectedProjectId,
                memberId: runnerRequestHost.session.memberId,
                canWrite: canProjectFeature('promptPresets.write'),
              }}
            />
            <DaemonTriggerDetailModalConnected
              run={(() => {
                const id = searchParams.get('runnerRequest');
                if (!id) return null;
                return { id, projectId: searchParams.get('projectId') ?? '' };
              })()}
              token={session?.token ?? null}
              memberId={session?.member?.id ?? null}
              activeContinueDraftTriggerId={daemonTriggerController.continueSourceTrigger?.id ?? null}
              onClose={() => {
                const params = new URLSearchParams(searchParams.toString());
                if (!params.has('runnerRequest')) return;
                params.delete('runnerRequest');
                const queryString = params.toString();
                router.replace(queryString.length > 0 ? `${pathname}?${queryString}` : pathname, { scroll: false });
              }}
            />
            <ConsentBanner />
            <SubscribeModal />
            <SubscriptionModal />
          </AppShellContextProvider>
        </PageActionsSlotProvider>
      </BreadcrumbProvider>
    </AuthGuard>
  );
};
